import LoadingPlaceHolder from '@/components/elements/LoadingPlaceholder';
import { useBookingFlowChrome } from '@/features/booking-flow/BookingFlowChromeContext';
import BookingFlowPage from '@/features/booking-flow/BookingFlowPage';
import { BOOKING_FLOW_STEP_SLUGS } from '@/features/booking-flow/constants';
import { deriveStepSelection } from '@/features/booking-flow/deriveStepSelection';
import { useCalendarOffersEmployeePick } from '@/features/booking-flow/useCalendarOffersEmployeePick';
import { buildTargetLocation, goToStep } from '@/features/booking-flow/goToStep';
import { getNextStep, getStepGuardTarget } from '@/features/booking-flow/steps';
import {
  serviceDetailsTrackingHandlers,
  trackEmployeeSelected,
  trackServiceSelected,
} from '@/features/booking-flow/tracking';
import { useBookingFlowSelection } from '@/features/booking-flow/useBookingFlowSelection';
import { parseSelection, serialiseSelection } from '@/features/booking-flow/urlState';
import withBookingFlowRedesignFlag from '@/features/booking-flow/withBookingFlowRedesignFlag';
import SelectedEmployeePanel from '@/features/booking-selection/components/SelectedEmployeePanel';
import { ANY_EMPLOYEE_ID } from '@/features/booking-selection/constants';
import SelectService from '@/features/booking-selection/views/SelectService';
import { useSelectService } from '@/features/booking-selection/views/SelectService.hooks';
import { getBookableEmployeeListItems } from '@/features/booking-selection/views/SelectEmployeeList.helpers';
import { useAppSelector } from '@/hooks';
import { _t } from '@/locale';
import { BookState } from '@/types/state/book';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';

/** The `/valj-tjanst` step page (U13). The employee panel renders in SelectService's own `employeePanel` slot, so `sideColumn` stays unset (KTD8). */
const SelectServicePage = () => {
  const { placeSlugId } = useParams<{ placeSlugId: string }>();
  const history = useHistory();
  const location = useLocation();
  const book = useAppSelector((state) => state.book) as BookState;
  const { user } = useAppSelector((state) => state.users);
  const place = book.place;
  useBookingFlowSelection(place);
  const calendarOffersEmployeePick = useCalendarOffersEmployeePick();
  const { attribution } = useBookingFlowChrome();

  const onPickService = (serviceId: number) => {
    if (!place) return;
    // Live read: a repeat click before re-render must not re-emit the pick.
    if (parseSelection(history.location.search).ss === String(serviceId)) return;
    const selection = deriveStepSelection(place, location.search, calendarOffersEmployeePick, user);
    const pickedService = (place.services ?? [])
      .flatMap((category) => category.services ?? [])
      .find((service) => service.id === serviceId);
    // Before the goToStep push, so a straight-to-calendar pick orders service_selected
    // before search_time, as the modal does. The pick replaces the selection (ss is set,
    // not appended), so the post-change service_ids is just this service.
    trackServiceSelected([String(serviceId)]);
    goToStep(
      history,
      placeSlugId,
      getNextStep(BOOKING_FLOW_STEP_SLUGS.SELECT_SERVICE, {
        ...selection,
        services: pickedService ? [pickedService] : [],
      }),
      { ss: String(serviceId) },
    );
  };

  // Hooks run unconditionally (before the guards below can return); tolerates an absent place.
  const viewProps = useSelectService({
    onPickService,
    ...serviceDetailsTrackingHandlers,
  });

  if (!place) {
    return (
      <BookingFlowPage title="">
        <LoadingPlaceHolder />
      </BookingFlowPage>
    );
  }

  const selection = deriveStepSelection(place, location.search, calendarOffersEmployeePick, user);
  const guardTarget = getStepGuardTarget(BOOKING_FLOW_STEP_SLUGS.SELECT_SERVICE, selection);
  if (guardTarget) {
    return <Redirect to={buildTargetLocation(placeSlugId, guardTarget, location.search)} />;
  }

  const { bookableEmployees, items: employees } = getBookableEmployeeListItems(
    place,
    (selection.services ?? []).map((service) => service.id),
  );

  const onPickEmployee = (employeeId: number) => {
    // sr carries about.externalId ('0' for any employee), never the internal id.
    const sr =
      employeeId === ANY_EMPLOYEE_ID
        ? String(ANY_EMPLOYEE_ID)
        : bookableEmployees.find((employee) => employee.id === employeeId)?.about?.externalId;
    if (!sr) return;
    // Live read: re-picking the already-selected row must not re-emit or re-replace.
    if (parseSelection(history.location.search).sr === sr) return;

    // Modal parity (U15): the employee change is a selection event, like the modal's
    // handleOnPickEmployee.
    trackEmployeeSelected(employeeId === ANY_EMPLOYEE_ID ? 'any' : 'specific');

    // Replace, not push: a pushed entry would sit behind the header's "Avbryt" replace
    // (KTD15), letting browser-back re-enter an abandoned flow with a stale employee.
    history.replace({ pathname: location.pathname, search: serialiseSelection(location.search, { sr }) });
  };

  // Only show a selection the panel has a row for: a stale sr naming a non-bookable
  // employee — or sr=0 with a single bookable one, where the any-employee row is not
  // rendered — would otherwise collapse narrow to an empty, un-expandable panel. An
  // unshowable selection means no panel, exactly like the sr-absent case.
  const hasMatchingPanelRow =
    selection.employee != null &&
    (selection.employee === ANY_EMPLOYEE_ID
      ? employees.length > 1
      : employees.some((item) => item.id === selection.employee));

  const employeePanel =
    hasMatchingPanelRow && !(place.about?.settings?.hideEmployees ?? false) ? (
      <SelectedEmployeePanel
        heading={_t('features.bookingSelection.SelectService.employeePanelHeading')}
        employees={employees}
        selectedEmployeeId={selection.employee ?? undefined}
        isNarrow={viewProps.isNarrow}
        onPick={onPickEmployee}
      />
    ) : undefined;

  return (
    <BookingFlowPage
      title={_t('features.bookingSelection.SelectService.title')}
      subtitle={place.about?.name}
      merchantLogoUrl={place.about?.profileImage}
      emitStepShown>
      <SelectService {...viewProps} employeePanel={employeePanel} footer={attribution?.('column')} />
    </BookingFlowPage>
  );
};

export default withBookingFlowRedesignFlag(SelectServicePage);
